import React, { useEffect, useState } from 'react';
import PresignedImage from './PresignedImage';
import { cacheAvatarFromElement, ensureAvatarCached, getCachedAvatar } from '../../services/rememberedAccounts';

/**
 * Profile-style avatar: local snapshot first, then network, then initials.
 */
const PresignedAvatar = ({
  fileKey,
  cacheKey,
  name,
  initials,
  className = 'w-6 h-6 rounded-full object-cover',
  fallbackClassName = 'flex items-center justify-center bg-primary-500 text-white text-xs font-medium',
}) => {
  const [localSrc, setLocalSrc] = useState(() => getCachedAvatar(fileKey, cacheKey));
  const [failed, setFailed] = useState(false);
  const fallbackText = initials
    ? String(initials).toUpperCase()
    : (name && name[0] ? name[0].toUpperCase() : 'U');

  useEffect(() => {
    setFailed(false);
    const existing = fileKey || cacheKey ? getCachedAvatar(fileKey, cacheKey) : null;
    if (existing) {
      setLocalSrc(existing);
      return undefined;
    }
    setLocalSrc(null);
    if (!fileKey) return undefined;
    let cancelled = false;
    ensureAvatarCached(fileKey, cacheKey).then((src) => {
      if (!cancelled && src) setLocalSrc(src);
    });
    return () => {
      cancelled = true;
    };
  }, [fileKey, cacheKey]);

  if (localSrc) {
    return (
      <img
        src={localSrc}
        alt={name || 'User'}
        className={className}
      />
    );
  }

  if (!fileKey || failed) {
    return (
      <div className={`${fallbackClassName} ${className} overflow-hidden`} aria-hidden={!name}>
        <span>{fallbackText}</span>
      </div>
    );
  }

  return (
    <PresignedImage
      fileKey={fileKey}
      alt={name || 'User'}
      className={className}
      onError={() => setFailed(true)}
      onLoad={(event) => cacheAvatarFromElement([fileKey, cacheKey], event.currentTarget)}
      loadingClassName={`${className} bg-gray-200 dark:bg-gray-700 animate-pulse`}
    />
  );
};

export default PresignedAvatar;
